import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
  IUseFilterButtonReturn,
  useFilterButton,
} from '@uniqkey-frontend/shared-app';
import {
  PartnerUserRole,
  PartnerUsersOrderProperty,
} from '@uniqkey-backend-partner/api-client';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import { IPartnerUsersTableRow } from '../../../components/tables/PartnerUsersTable';
import {
  IGetPartnerUsersParams,
  TGetPartnerUsersMethod,
} from '../../usePartnerUsersAPI/interfaces';
import usePartnerUsersAPI from '../../usePartnerUsersAPI';
import {
  IPartnerUsersListFilterSubmitResult,
} from '../../../pages/PartnerUsersPage/components/PartnerUsersListFilter';

export const REACT_QUERY_PARTNER_USERS_KEY = [ReactQueryKeyEnum.PartnerUsers];

interface IPersistentFilters extends Pick<
  IGetPartnerUsersParams, 'partnerId'
> {}

export interface IUsePartnerUsersTableParams {
  persistentFilters?: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUsePartnerUsersTableReturn extends
  Omit<IUseTableReturn<IPartnerUsersTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<IUseQueryTableFetchReturn<TGetPartnerUsersMethod>, 'data' | 'isFetchedAfterMount'>,
  IUseFilterButtonReturn
{
  partnerUsers: IUseQueryTableFetchReturn<TGetPartnerUsersMethod>['data'];
  selectedPartnerUsers: IUseTableReturn<IPartnerUsersTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetPartnerUsersParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<IGetPartnerUsersParams['searchQuery']>>;
  filterValues: IPartnerUsersListFilterSubmitResult;
  setFilterValues: Dispatch<SetStateAction<IPartnerUsersListFilterSubmitResult>>
}

interface ITablePreservationConfigs extends
  Omit<IGetPartnerUsersParams,
    'page' | 'pageLength' | 'partnerId' | 'orderPropertyName' | 'isDescending'
  >,
  Pick<IUseTableReturn<IPartnerUsersTableRow>, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetPartnerUsersParams['page'];
  perPage: IGetPartnerUsersParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: PartnerUsersOrderProperty },
  ),
  columnOrderDirection: true,
  roleFilter: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: PartnerUserRole },
  ),
};

const usePartnerUsersTable = (
  params: IUsePartnerUsersTableParams,
): IUsePartnerUsersTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const { getPartnerUsers } = usePartnerUsersAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    searchQuery: initialSearchQuery,
    roleFilter: initialRoleFilter,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetPartnerUsersParams['searchQuery']
  >(() => initialSearchQuery);
  const [filterValues, setFilterValues] = useState<
    IPartnerUsersListFilterSubmitResult
  >({
    roleFilter: initialRoleFilter,
  });

  const { isFilterActive, numberOfActiveFilters } = useFilterButton(filterValues);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedPartnerUsers,
    resetTableMethods,
    ...restTableProps
  } = useTable<IPartnerUsersTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
    setFilterValues({
      roleFilter: undefined,
    });
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: partnerUsers, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_PARTNER_USERS_KEY,
    queryClient,
    request: getPartnerUsers,
    params: {
      page: activePage,
      pageLength: perPage,
      roleFilter: filterValues.roleFilter,
      searchQuery,
      orderPropertyName: PartnerUsersOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof PartnerUsersOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
    autoRefetch: true,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isFilterActive,
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
    roleFilter: filterValues.roleFilter,
  });

  return {
    partnerUsers,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedPartnerUsers,
    localization,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default usePartnerUsersTable;
